import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';
import { Button, Container, Form, Input, Typography } from '@app/components';
import { usePlanLimits } from '@app/domain/plan/hooks/usePlanLimits';
import { GlobalSettingsBanner } from '@app/domain/qr-code/components/settings/GlobalSettingsBanner';
import { ShortURLWarningBanner } from '@app/domain/qr-code/components/settings/ShortURLWarningBanner';
import { replaceSubdomain } from '@app/domain/qr-code/qr-code.utils';
import { useRegisterSubdomain } from '@app/domain/subdomain/api/subdomain.hooks';
import { SubdomainRegistrationInDtoSchema } from '@app/domain/subdomain/api/subdomain.schema';
import { useUpdateCustomer } from '@app/domain/user/api/user.hooks.api';
import { useStore } from '@app/store/useStore.hook';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export const AccountSetUpShortURL: React.FC = () => {
  const { t } = useTranslation();
  const { canUseSubdomain } = usePlanLimits();
  const { isOldPlanUser } = useAuthorizedUser();

  const { currentUser: user } = useStore(({ currentUser }) => ({
    currentUser,
  }));
  const userSubdomain = user?.subdomain;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(SubdomainRegistrationInDtoSchema),
    defaultValues: { subdomain: userSubdomain || '' },
  });

  const { mutate: updateCustomer } = useUpdateCustomer();
  const { mutate: registerSubdomain, isLoading } = useRegisterSubdomain();

  const subdomain = watch('subdomain');

  const handlerRegisterSubdomain = async () => {
    if (!canUseSubdomain) {
      console.error('Invalid subdomain value', { subdomain });
      return;
    }

    if (!subdomain && userSubdomain) {
      await registerSubdomain(
        { subdomain },
        {
          onSuccess: async () => {
            toast.success(t('toaster.subdomainDeletion'));
            await updateCustomer({ isSubdomainEnabled: false, subdomain: null });
          },
        }
      );
      return;
    }

    await registerSubdomain(
      { subdomain },
      {
        onSuccess: () => {
          toast.success(t('toaster.subdomainCreation'));
        },
      }
    );
  };

  console.log(replaceSubdomain(''));

  const canSetUpDomain =
    !canUseSubdomain ||
    userSubdomain === subdomain ||
    (!subdomain && !userSubdomain) ||
    !!errors.subdomain ||
    isOldPlanUser;

  return (
    <Container className="flex flex-col gap-6 lg:rounded-xl">
      <div className="flex flex-col gap-4">
        <Typography className="text-lg text-gray-800 font-bold">{t('whiteLabel.shortURL.title')}</Typography>
        <Typography className="text-s text-gray-800 ">{t('whiteLabel.shortURL.description')}</Typography>
      </div>
      <div className="flex flex-col gap-2">
        <Typography className="text-s text-gray-800 ">{t('whiteLabel.shortURL.previewTitle')}</Typography>
        <Typography className="text-lg text-gray-800 font-medium break-words">
          {replaceSubdomain(subdomain || '')}
          {'{QRID}'}
        </Typography>
      </div>
      <Form control={control} onSubmit={handleSubmit(handlerRegisterSubdomain)} className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <Controller
            name="subdomain"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('whiteLabel.shortURL.labelSubdomain')}
                placeholder={t('whiteLabel.shortURL.placesholder')}
                withBG
                disabled={!canUseSubdomain}
                errorMessage={errors.subdomain?.message}
              />
            )}
          />
        </div>
        {canUseSubdomain && <GlobalSettingsBanner />}
        <ShortURLWarningBanner
          withWrapper={
            canUseSubdomain &&
            ((!!userSubdomain && subdomain !== userSubdomain) || (!userSubdomain && !!subdomain)) &&
            !isOldPlanUser
          }
        >
          <div className="gap-4 flex">
            <Button
              variant="outlined"
              type="button"
              onClick={() => setValue('subdomain', userSubdomain || '')}
              disabled={!canUseSubdomain || (!userSubdomain && !subdomain) || subdomain === userSubdomain}
            >
              {t('whiteLabel.shortURL.cancelButton')}
            </Button>
            <Button
              type="submit"
              disabled={canSetUpDomain}
              loading={isLoading}
              {...(!subdomain && userSubdomain && { color: 'error' })}
            >
              {!subdomain && userSubdomain
                ? t('whiteLabel.shortURL.deleteButton')
                : !subdomain
                ? t('whiteLabel.shortURL.setUpButton')
                : t('whiteLabel.shortURL.updateButton')}
            </Button>
          </div>
        </ShortURLWarningBanner>
      </Form>
    </Container>
  );
};
